// User preferences service for VAT settings
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc,
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';

export const userPreferencesService = {
  /**
   * Get all user preferences for a specific user and company
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @returns {Promise<Object|null>} User preferences object or null if not found
   */
  async getUserPreferences(userId, companyId) {
    try {
      const preferencesRef = doc(db, 'userPreferences', `${userId}_${companyId}`);
      const snapshot = await getDoc(preferencesRef);
      
      if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user preferences:', error);
      throw error;
    }
  },

  /**
   * Update VAT preference for a specific cost type (fuel or maintenance)
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @param {'fuel'|'maintenance'} costType - The cost type
   * @param {boolean} includesVAT - Whether the cost type includes VAT by default
   * @returns {Promise<boolean>} Success status
   */
  async updateVATPreference(userId, companyId, costType, includesVAT) {
    try {
      if (!['fuel', 'maintenance'].includes(costType)) {
        throw new Error('Invalid cost type. Must be "fuel" or "maintenance"');
      }

      const preferencesRef = doc(db, 'userPreferences', `${userId}_${companyId}`);
      
      // Get existing preferences or create new structure
      const existingPrefs = await this.getUserPreferences(userId, companyId);
      
      const updatedPreferences = {
        userId,
        companyId,
        preferences: {
          ...existingPrefs?.preferences,
          costingPreferences: {
            ...existingPrefs?.preferences?.costingPreferences,
            [`${costType}VATDefault`]: includesVAT,
            lastUpdated: serverTimestamp()
          }
        },
        updatedAt: serverTimestamp()
      };

      // If this is a new preference document, add createdAt
      if (!existingPrefs) {
        updatedPreferences.createdAt = serverTimestamp();
      }

      await setDoc(preferencesRef, updatedPreferences, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating VAT preference:', error);
      throw error;
    }
  },

  /**
   * Get VAT preference for a specific cost type with comprehensive error handling
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @param {'fuel'|'maintenance'} costType - The cost type
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Result with preference value and error info
   */
  async getVATPreference(userId, companyId, costType, options = {}) {
    const { returnDetails = false, timeout = 5000 } = options;
    
    try {
      // Validate input parameters
      if (!userId || typeof userId !== 'string') {
        throw new Error('Valid user ID is required');
      }
      
      if (!companyId || typeof companyId !== 'string') {
        throw new Error('Valid company ID is required');
      }
      
      if (!['fuel', 'maintenance'].includes(costType)) {
        throw new Error('Invalid cost type. Must be "fuel" or "maintenance"');
      }

      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });
      
      const preferencesPromise = this.getUserPreferences(userId, companyId);
      const preferences = await Promise.race([preferencesPromise, timeoutPromise]);
      
      // Return default value (true - VAT included) if no preferences exist
      if (!preferences?.preferences?.costingPreferences) {
        const result = {
          value: true,
          isDefault: true,
          source: 'system_default'
        };
        
        return returnDetails ? result : result.value;
      }

      const vatPreference = preferences.preferences.costingPreferences[`${costType}VATDefault`];
      
      // Return default value (true) if specific preference is not set
      const preferenceValue = vatPreference !== undefined ? vatPreference : true;
      const result = {
        value: preferenceValue,
        isDefault: vatPreference === undefined,
        source: vatPreference !== undefined ? 'user_preference' : 'field_default',
        lastUpdated: preferences.preferences.costingPreferences.lastUpdated
      };
      
      return returnDetails ? result : result.value;
      
    } catch (error) {
      console.error('Error getting VAT preference:', error);
      
      const fallbackResult = {
        value: true,
        isDefault: true,
        source: 'error_fallback',
        error: error.message
      };
      
      // Return default value on error to prevent UI blocking
      return returnDetails ? fallbackResult : fallbackResult.value;
    }
  },

  /**
   * Get both fuel and maintenance VAT preferences at once with error handling
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Object with fuel and maintenance VAT preferences
   */
  async getVATPreferences(userId, companyId, options = {}) {
    const { returnDetails = false, retryCount = 2 } = options;
    
    let attempt = 0;
    let lastError = null;
    
    while (attempt <= retryCount) {
      try {
        const [fuelResult, maintenanceResult] = await Promise.all([
          this.getVATPreference(userId, companyId, 'fuel', { returnDetails: true }),
          this.getVATPreference(userId, companyId, 'maintenance', { returnDetails: true })
        ]);

        const result = {
          fuelVATDefault: fuelResult.value,
          maintenanceVATDefault: maintenanceResult.value
        };
        
        if (returnDetails) {
          result.details = {
            fuel: fuelResult,
            maintenance: maintenanceResult,
            loadedSuccessfully: true,
            attempt: attempt + 1
          };
        }
        
        return result;
        
      } catch (error) {
        lastError = error;
        attempt++;
        
        if (attempt <= retryCount) {
          const currentAttempt = attempt; // Capture current attempt value
          console.warn(`Attempt ${currentAttempt} failed, retrying...`, error);
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, currentAttempt) * 1000));
        }
      }
    }
    
    console.error('Error getting VAT preferences after all retries:', lastError);
    
    // Return default values on error
    const fallbackResult = {
      fuelVATDefault: true,
      maintenanceVATDefault: true
    };
    
    if (returnDetails) {
      fallbackResult.details = {
        fuel: { value: true, isDefault: true, source: 'error_fallback', error: lastError?.message },
        maintenance: { value: true, isDefault: true, source: 'error_fallback', error: lastError?.message },
        loadedSuccessfully: false,
        attempt: attempt,
        error: lastError?.message
      };
    }
    
    return fallbackResult;
  },

  /**
   * Initialize default preferences for a new user
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @returns {Promise<boolean>} Success status
   */
  async initializeDefaultPreferences(userId, companyId) {
    try {
      const preferencesRef = doc(db, 'userPreferences', `${userId}_${companyId}`);
      
      // Check if preferences already exist
      const existingPrefs = await this.getUserPreferences(userId, companyId);
      if (existingPrefs) {
        return true; // Already initialized
      }

      const defaultPreferences = {
        userId,
        companyId,
        preferences: {
          costingPreferences: {
            fuelVATDefault: true, // Default: fuel amounts include VAT
            maintenanceVATDefault: true, // Default: maintenance amounts include VAT
            lastUpdated: serverTimestamp()
          }
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(preferencesRef, defaultPreferences);
      return true;
    } catch (error) {
      console.error('Error initializing default preferences:', error);
      throw error;
    }
  },

  /**
   * Update multiple VAT preferences at once with comprehensive error handling
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @param {Object} preferences - Object with fuel and/or maintenance VAT preferences
   * @param {Object} options - Additional options
   * @returns {Promise<boolean>} True if successful, throws error if failed
   */
  async updateVATPreferences(userId, companyId, preferences, options = {}) {
    const { retryCount = 2, validateInput = true } = options;
    
    let attempt = 0;
    let lastError = null;
    
    try {
      // Validate input parameters
      if (validateInput) {
        if (!userId || typeof userId !== 'string') {
          throw new Error('Valid user ID is required');
        }
        
        if (!companyId || typeof companyId !== 'string') {
          throw new Error('Valid company ID is required');
        }
        
        if (!preferences || typeof preferences !== 'object') {
          throw new Error('Valid preferences object is required');
        }
        
        const { fuelVATDefault, maintenanceVATDefault } = preferences;
        
        if (fuelVATDefault !== undefined && typeof fuelVATDefault !== 'boolean') {
          throw new Error('fuelVATDefault must be a boolean value');
        }
        
        if (maintenanceVATDefault !== undefined && typeof maintenanceVATDefault !== 'boolean') {
          throw new Error('maintenanceVATDefault must be a boolean value');
        }
        
        if (fuelVATDefault === undefined && maintenanceVATDefault === undefined) {
          throw new Error('At least one preference must be specified');
        }
      }
      
      while (attempt <= retryCount) {
        try {
          const { fuelVATDefault, maintenanceVATDefault } = preferences;
          
          const preferencesRef = doc(db, 'userPreferences', `${userId}_${companyId}`);
          
          // Get existing preferences or create new structure
          const existingPrefs = await this.getUserPreferences(userId, companyId);
          
          const updatedPreferences = {
            userId,
            companyId,
            preferences: {
              ...existingPrefs?.preferences,
              costingPreferences: {
                ...existingPrefs?.preferences?.costingPreferences,
                ...(fuelVATDefault !== undefined && { fuelVATDefault }),
                ...(maintenanceVATDefault !== undefined && { maintenanceVATDefault }),
                lastUpdated: serverTimestamp()
              }
            },
            updatedAt: serverTimestamp()
          };

          // If this is a new preference document, add createdAt
          if (!existingPrefs) {
            updatedPreferences.createdAt = serverTimestamp();
          }

          await setDoc(preferencesRef, updatedPreferences, { merge: true });
          
          return true;
          
        } catch (error) {
          lastError = error;
          attempt++;
          
          if (attempt <= retryCount) {
            const currentAttempt = attempt; // Capture current attempt value
            console.warn(`Update attempt ${currentAttempt} failed, retrying...`, error);
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, currentAttempt) * 1000));
          }
        }
      }
      
      throw lastError;
      
    } catch (error) {
      console.error('Error updating VAT preferences:', error);
      throw error;
    }
  },

  /**
   * Validate preferences data structure
   * @param {Object} preferences - Preferences object to validate
   * @returns {Object} Validation result
   */
  validatePreferencesData(preferences) {
    const errors = [];
    const warnings = [];
    
    if (!preferences || typeof preferences !== 'object') {
      errors.push('Preferences must be an object');
      return { isValid: false, errors, warnings };
    }
    
    // Check for required structure
    if (!preferences.preferences || typeof preferences.preferences !== 'object') {
      errors.push('Preferences must contain a preferences object');
    }
    
    if (preferences.preferences?.costingPreferences) {
      const costingPrefs = preferences.preferences.costingPreferences;
      
      // Validate fuel preference
      if (costingPrefs.fuelVATDefault !== undefined && typeof costingPrefs.fuelVATDefault !== 'boolean') {
        errors.push('fuelVATDefault must be a boolean value');
      }
      
      // Validate maintenance preference
      if (costingPrefs.maintenanceVATDefault !== undefined && typeof costingPrefs.maintenanceVATDefault !== 'boolean') {
        errors.push('maintenanceVATDefault must be a boolean value');
      }
      
      // Check for timestamp
      if (!costingPrefs.lastUpdated) {
        warnings.push('lastUpdated timestamp is missing');
      }
    }
    
    // Validate user and company IDs
    if (!preferences.userId || typeof preferences.userId !== 'string') {
      errors.push('Valid userId is required');
    }
    
    if (!preferences.companyId || typeof preferences.companyId !== 'string') {
      errors.push('Valid companyId is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  },

  /**
   * Get preferences with fallback and error recovery
   * @param {string} userId - The user ID
   * @param {string} companyId - The company ID
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Preferences with fallback handling
   */
  async getPreferencesWithFallback(userId, companyId, options = {}) {
    const { useCache = true, cacheTimeout = 300000 } = options; // 5 minutes cache
    
    try {
      // Try to get preferences normally
      const preferences = await this.getVATPreferences(userId, companyId, { returnDetails: true });
      
      if (preferences.details?.loadedSuccessfully) {
        return {
          ...preferences,
          source: 'database',
          reliable: true
        };
      }
      
      // If loading failed, return fallback
      return {
        fuelVATDefault: true,
        maintenanceVATDefault: true,
        source: 'fallback',
        reliable: false,
        error: preferences.details?.error
      };
      
    } catch (error) {
      console.error('Failed to get preferences with fallback:', error);
      
      return {
        fuelVATDefault: true,
        maintenanceVATDefault: true,
        source: 'error_fallback',
        reliable: false,
        error: error.message
      };
    }
  }
};